import { sameAddress } from '../../../shared/routeAnchor';
import type { DayRouteRow, IsoDate, RouteSettings, StoredRoute } from '../../../shared/types';
import { sanitizeRoute } from '../../../shared/routeRetention';
import { addDays, todayLocal } from '../logic/dates';
import { getDb } from './db';
import { enqueue, listOutbox } from './outbox';
import type { EnqueueInput } from './outbox';
import { storeEvents } from './store';

/** One route per day in the account (D84). The device keeps its working copy by date in localStorage,
 *  so the Route tab works offline; every save is queued for the server through the outbox (D3) and
 *  the sync brings the other device's routes back. Last writer wins by `updated_at`. */

const ROUTES_KEY = 'sb.route.v2';
/** D27's device-only route: moved into today's synced route once, then removed. */
const LEGACY_KEY = 'sb.route.v1';
const SETTINGS_KEY = 'route_settings';
const LONG_AGO = '1970-01-01T00:00:00.000Z';

/** Fired on `window` when the sync changed a day's route, so an open Route tab redraws. */
export const ROUTE_CHANGED = 'sb:route-changed';

type RouteMap = Record<IsoDate, StoredRoute>;

const isRoute = (r: unknown): r is StoredRoute =>
  !!r && typeof r === 'object' && !!(r as StoredRoute).anchor && Array.isArray((r as StoredRoute).stops);

const readMap = (): RouteMap => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(ROUTES_KEY) ?? '{}');
    const map = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as RouteMap) : {};
    const safe = Object.fromEntries(Object.entries(map).filter(([, r]) => isRoute(r)).map(([day, r]) => [day, sanitizeRoute(r)]));
    try { localStorage.setItem(ROUTES_KEY, JSON.stringify(safe)); }
    catch (err) { console.error('Route cleanup could not be persisted', err); }
    return safe;
  } catch {
    return {};
  }
};

const writeMap = (map: RouteMap): void => localStorage.setItem(ROUTES_KEY, JSON.stringify(map));

/** Queue for the server and wake the sync (its triggers turn `write` into a kick). */
const queue = async (op: EnqueueInput): Promise<void> => {
  await enqueue(op);
  storeEvents.dispatchEvent(new Event('write'));
};

/** For the saves nobody waits for. The route is already on the device by the time the queue runs,
 *  so a queue that fails is said once here rather than left as an unhandled rejection in whichever
 *  view fired it. Callers that can tell the rep something better (routePlan's Accept) still await. */
export const unwatched = (saving: Promise<void>): void => {
  void saving.catch((err: unknown) => console.error('Route could not be queued', err));
};

const putOp = (route: StoredRoute & { day: IsoDate }, accepted: boolean): EnqueueInput => ({
  method: 'PUT',
  path: `/api/daily/routes/${route.day}`,
  body: { route, area: route.area ?? null, ...(accepted ? { accepted: true } : {}) },
  label: `Route · ${route.day}`,
});

/** The old route keeps its own age, so an Accept made on the other device stays the newer one. */
const takeLegacy = (map: RouteMap): RouteMap => {
  const raw = localStorage.getItem(LEGACY_KEY);
  if (raw === null) return map;
  localStorage.removeItem(LEGACY_KEY);
  const today = todayLocal();
  let old: unknown;
  try {
    old = JSON.parse(raw);
  } catch {
    return map;
  }
  if (!isRoute(old) || map[today]) return map;
  const age = Date.parse(old.created_at);
  const route = {
    ...sanitizeRoute(old),
    day: today,
    updated_at: Number.isNaN(age) ? LONG_AGO : new Date(age).toISOString(),
  };
  const next = { ...map, [today]: route };
  writeMap(next);
  // the route is kept on the device either way; the next save queues it again
  unwatched(queue(putOp(route, false)));
  return next;
};

const routes = (): RouteMap => takeLegacy(readMap());

export const loadRoute = (day: IsoDate = todayLocal()): StoredRoute | null => {
  const route = routes()[day];
  return isRoute(route) ? route : null;
};

/** Saves the route as its day's (today when it names none) and queues it for the account. */
export const saveRoute = (route: StoredRoute, opts: { accepted?: boolean } = {}): Promise<void> => {
  const day = route.day ?? todayLocal();
  const saved = { ...sanitizeRoute(route), day, updated_at: new Date().toISOString() };
  writeMap({ ...routes(), [day]: saved });
  return queue(putOp(saved, opts.accepted === true));
};

export const clearRoute = (day: IsoDate = todayLocal()): Promise<void> => {
  const { [day]: _gone, ...rest } = routes();
  writeMap(rest);
  return queue({ method: 'DELETE', path: `/api/daily/routes/${day}`, label: `Clear route · ${day}` });
};

const ROUTE_PATH = /^\/api\/daily\/routes\/(\d{4}-\d{2}-\d{2})$/;

/** The days whose save is still in the outbox: refused by the server (parked) or on its way. */
const unsentDays = async (): Promise<Set<string>> =>
  new Set(
    (await listOutbox()).flatMap((op) => {
      const day = op.method === 'PUT' ? op.path.match(ROUTE_PATH)?.[1] : undefined;
      return day ? [day] : [];
    }),
  );

/** The account's routes from the sync replace this device's from yesterday on; older days are let go.
 *  A local copy newer than the server's is kept, and so is a day whose save the server has not taken
 *  (refused, or still on its way): the rep's route never vanishes from the phone. */
export const applyServerRoutes = async (rows: readonly DayRouteRow[]): Promise<void> => {
  const from = addDays(todayLocal(), -1);
  const unsent = await unsentDays();
  const local = routes();
  const next: RouteMap = {};
  for (const [day, mine] of Object.entries(local))
    if (day >= from && unsent.has(day) && isRoute(mine)) next[day] = mine;
  for (const row of rows) {
    if (row.day < from || !isRoute(row.route) || next[row.day]) continue;
    const mine = local[row.day];
    next[row.day] =
      mine?.updated_at && mine.updated_at > row.updated_at ? mine : { ...sanitizeRoute(row.route), day: row.day };
  }
  if (JSON.stringify(next) === JSON.stringify(local)) return;
  writeMap(next);
  window.dispatchEvent(new Event(ROUTE_CHANGED));
};

/** Plan tomorrow's settings as the last sync brought them (D83), for planning offline-aware screens. */
export const keepRouteSettings = async (settings: RouteSettings): Promise<void> => {
  await (await getDb()).put('meta', { key: SETTINGS_KEY, value: {...settings, warm_priority: settings.warm_priority === true} });
};

export const loadRouteSettings = async (): Promise<RouteSettings | null> => {
  const settings = (await (await getDb()).get('meta', SETTINGS_KEY))?.value as RouteSettings | undefined;
  return settings ? {...settings, warm_priority: settings.warm_priority === true} : null;
};

export const installRouteRetention = (): void => {
  const clean = () => {
    routes();
    void listOutbox().catch((err: unknown) => console.error('Route queue cleanup failed', err));
  };
  clean();
  window.addEventListener('pageshow', clean);
  document.addEventListener('visibilitychange', clean);
};

/** Existing retained routes are the only reuse store. Copy inputs, never evidence across days. */
export const retainedMeetingPoints = () => Object.values(routes()).flatMap(r =>
  r.anchor.kind === 'census_entry' ? [{ day: r.day, entry: r.anchor.entry }] : []);

/** Synchronous, best-effort acknowledgement: local storage failure cannot stall accepted FIFO work. */
export const applyRoutePutResult = (op: Pick<EnqueueInput, 'method' | 'path' | 'body'>, value: unknown): void => {
  try {
    const day = op.method === 'PUT' ? op.path.match(ROUTE_PATH)?.[1] : undefined;
    if (!day || !value || typeof value !== 'object') return;
    const row = value as DayRouteRow;
    const submitted = (op.body as { route?: unknown } | undefined)?.route;
    if (row.day !== day || typeof row.updated_at !== 'string' || !Number.isFinite(Date.parse(row.updated_at)) || !isRoute(row.route) || !isRoute(submitted)) return;
    if (row.route.day !== day || row.route.updated_at !== row.updated_at) return;
    const local = routes();
    const current = local[day];
    if (!current) return;
    const sent = sanitizeRoute(submitted);
    const returned = sanitizeRoute(row.route);
    let next: StoredRoute | undefined;
    if (JSON.stringify(current) === JSON.stringify(sent)) next = { ...returned, day, updated_at: row.updated_at };
    else {
      const a = current.anchor, b = sent.anchor, c = returned.anchor;
      if ((a.kind === 'unresolved' || a.kind === 'census_entry') && (b.kind === 'unresolved' || b.kind === 'census_entry') && (c.kind === 'unresolved' || c.kind === 'census_entry') &&
        a.entry && b.entry && c.entry && a.entry_attempt && a.entry_attempt === b.entry_attempt && b.entry_attempt === c.entry_attempt && sameAddress(a.entry, b.entry) && sameAddress(b.entry, c.entry))
        next = { ...current, anchor: { ...c, entry: a.entry } };
    }
    if (!next || JSON.stringify(next) === JSON.stringify(current)) return;
    writeMap({ ...local, [day]: next });
    window.dispatchEvent(new Event(ROUTE_CHANGED));
  } catch (err) { console.error('Route acknowledgement could not be applied', err); }
};
